import React, { useCallback, useEffect, useRef, useState } from "react";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import MoreVertIcon from "@mui/icons-material/MoreVert";
// import RemoveCircle from "@mui/icons-material/RemoveCircle";
import Clear from "@mui/icons-material/Clear";
import Add from "@mui/icons-material/Add";
import { IconButton } from "@mui/material";
// import CloseIcon from "@mui/icons-material/Close";
// import WarningIcon from "@mui/icons-material/Warning";
import { CameraSettings, Presets, SnapshotCamera, Bookmark, Video } from "orion-components/CBComponents/Icons";
import PropTypes from "prop-types";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import NestedMenu from "./Components/NestedMenu";
import { getTranslation } from "orion-components/i18n";
import { useDispatch, useSelector } from "react-redux";
import { getDir } from "orion-components/i18n/Config/selector";
import { cameraService, downloadsService } from "client-app-core";
import { removeDockedCameraAndState, addCameraToDockMode } from "orion-components/Dock/Cameras/actions";
import { canManageByApplication, checkPermissionBasedOnFeedId } from "orion-components/Profiles/Selectors";
import LockIcon from "@mui/icons-material/Lock";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import { canAccessPtzLockMenu, getIsCameraExplicitlyLocked } from "../../../../GlobalData/PTZPrivilege/selectors";
import { createUserFeedback } from "orion-components/Dock/Actions/index.js";
import { updateComponentInstance } from "orion-components/SharedComponents/ComponentState/redux/actions";
import { makeSelectComponentProp } from "../../../../SharedComponents/ComponentState/redux/selectors";
import CameraSettingsDialog from "./Components/CameraSettingsDialog";
import AddPresetDialog from "./Components/AddPresetDialog";
import DeletePresetDialog from "./Components/DeletePresetDialog";
import DeleteBookmarkDialog from "./Components/DeleteBookmarkDialog";
import DeleteClipDialog from "./Components/DeleteClipDialog";
import { handleSnapshotCapture } from "./snapshotCapture";
import EventBus from "orion-components/SharedComponents/EventBus";

const COMPONENT_KEY = "cbComponents.newVideoPlayer.videoPlayerContainer";

const getRequestErrorMessage = (err, response, fallback) => {
	const reasonMessage = response?.reason?.message;
	return (
		err?.message ||
		response?.message ||
		(typeof reasonMessage === "string" ? reasonMessage : reasonMessage?.message) ||
		(typeof response?.reason === "string" ? response.reason : null) ||
		fallback
	);
};

const MainMenu = ({
	getMenuState,
	camera,
	dockedItemId,
	isDockedVideoTour,
	readOnly,
	inDock,
	tourMode,
	isInVideoWall,
	removeFromWall,
	clusterProps,
	isOnAlertProfile,
	hideRemoveFromWall,
	instanceId,
	onBookmarkSelect,
	onClipSelect,
	dialogContainerRef
}) => {
	const dispatch = useDispatch();

	const [displayMenu, setDisplayMenu] = useState(null);
	const [displayNestedMenu, setDisplayNestedMenu] = useState(false);
	const [nestedMenuIsFor, setNestedMenuIsFor] = useState("");
	const [presets, setPresets] = useState([]);
	const isLocked = useSelector(state => getIsCameraExplicitlyLocked(state, { cameraId: camera.id }));
	const timeFormat = useSelector(state => state.appState.global.timeFormat);
	const locale = useSelector(state => state.i18n.locale);
	const [nestedMenuContent, setNestedMenuContent] = useState([]);
	const dir = useSelector(state => getDir(state));
	const canManage = useSelector(state => canManageByApplication(state, "camera-wall-app", "manage"));
	const canManageCameraFeed = useSelector(state =>
		checkPermissionBasedOnFeedId(state)(camera?.feedId, "permissions", "manage")
	);
	const canShowLockMenu = useSelector(state => canAccessPtzLockMenu(state, { cameraId: camera.id }));
	const [showCameraSettings, setShowCameraSettings] = useState(false);
	const [showAddPresetDialog, setShowAddPresetDialog] = useState(false);
	const [showDeletePresetDialog, setShowDeletePresetDialog] = useState(false);
	const [showDeleteBookmarkDialog, setShowDeleteBookmarkDialog] = useState(false);
	const [showDeleteClipDialog, setShowDeleteClipDialog] = useState(false);
	const [isSavingPreset, setIsSavingPreset] = useState(false);
	const [isDeletingPreset, setIsDeletingPreset] = useState(false);
	const [isDeletingBookmark, setIsDeletingBookmark] = useState(false);
	const [isDeletingClip, setIsDeletingClip] = useState(false);
	const [isExportingClip, setIsExportingClip] = useState(false);
	const [isPresetValid, setIsPresetValid] = useState(true);
	const [selectedPresetToDelete, setSelectedPresetToDelete] = useState(null);
	const [selectedBookmarkToDelete, setSelectedBookmarkToDelete] = useState(null);
	const [selectedClipToDelete, setSelectedClipToDelete] = useState(null);
	const [bookmarks, setBookmarks] = useState([]);
	const [clips, setClips] = useState([]);
	const mainMenuRef = useRef(null);
	const user = useSelector(state => state?.session?.user?.profile);
	const canControl =
		!readOnly &&
		user?.integrations &&
		user.integrations.find(int => int.intId === camera.feedId) &&
		user.integrations.find(int => int.intId === camera.feedId).permissions &&
		user.integrations.find(int => int.intId === camera.feedId).permissions.includes("control");
	const hasControlCapability = camera?.entityData?.properties?.features?.includes("control") === true;

	useEffect(() => {
		if (!instanceId) return; // optional guard
		dispatch(
			updateComponentInstance(COMPONENT_KEY, instanceId, { components: { mainMenu: { showControls: true } } })
		);
	}, [dispatch, instanceId]);

	const setupPresets = useCallback(async () => {
		if (!hasControlCapability || !canControl) {
			setPresets([]);
			return;
		}

		const response = await cameraService.getPresets(camera.id);
		const data = response?.result?.data;
		const hasValidData = response?.result?.status === "SUCCESS" && Array.isArray(data);

		if (!hasValidData) {
			setIsPresetValid(false);
			return;
		}
		setIsPresetValid(true);
		setPresets(data);
	}, [camera.id, canControl, hasControlCapability]);

	const setupBookmarks = useCallback(async () => {
		cameraService.getBookmarks(camera.id, (err, res) => {
			if (err || !res || res.success === false) {
				// Handle error case if needed
				console.error("Failed to fetch bookmarks", err, res);
				return;
			}

			setBookmarks(res);
		});
	}, [camera.id]);

	const setupVideoClips = useCallback(async () => {
		cameraService.getVideoClips(camera.id, (err, res) => {
			if (err || !res || res.success === false) {
				console.error("Failed to fetch video clips", err, res);
				return;
			}

			setClips(res);
		});
	}, [camera.id]);

	useEffect(() => {
		const handleBookmarksUpdated = data => {
			if (data?.cameraId === camera.id) {
				setupBookmarks();
			}
		};

		EventBus.subscribe("CameraBookmarks_updated", handleBookmarksUpdated);

		return () => {
			EventBus.unsubscribe("CameraBookmarks_updated", handleBookmarksUpdated);
		};
	}, [camera.id, setupBookmarks]);

	useEffect(() => {
		const handleVideoClipsUpdated = data => {
			if (data?.cameraId === camera.id) {
				setupVideoClips();
			}
		};

		EventBus.subscribe("CameraVideoClips_updated", handleVideoClipsUpdated);

		return () => {
			EventBus.unsubscribe("CameraVideoClips_updated", handleVideoClipsUpdated);
		};
	}, [camera.id, setupVideoClips]);

	const dockedCameras = useSelector(state => state.appState.dock?.cameraDock?.dockedCameras);
	const cameraIsDocked = dockedCameras && dockedCameras.includes(camera.id);
	const videoTourIsDocked = isDockedVideoTour && dockedItemId && dockedCameras?.includes(dockedItemId);

	const hasCapability = capability => {
		return camera.entityData.properties.features && camera.entityData.properties.features.includes(capability);
	};

	useEffect(() => {
		setupPresets();
		setupBookmarks();
		setupVideoClips();
	}, [setupBookmarks, setupPresets, setupVideoClips]);

	const mainMenuShowControls = useSelector(
		makeSelectComponentProp(COMPONENT_KEY, instanceId, "components.mainMenu.showControls")
	);

	const canChangeSettings = hasCapability("focus") || hasCapability("iris");
	const canCreatePreset = hasCapability("createPreset");
	const canDeletePreset = hasCapability("deletePreset");

	// Currently all players except avigilon support client side snapshot capture
	const supportsClientSnapshot = camera?.player?.type !== "avigilon";
	// We still need to check camera features to see if the integration supports server side snapshot
	const supportsServerSnapshot = hasCapability("snapshot");

	const menuItems = [
		{
			id: 1,
			canAccessMenu: !tourMode && hasCapability("control") && canControl,
			item: mainMenuShowControls
				? getTranslation("global.CBComponents.newVideoPlayer.components.mainMenu.hideControls")
				: getTranslation("global.CBComponents.newVideoPlayer.components.mainMenu.showControls"),
			icon: mainMenuShowControls ? (
				<VisibilityIcon sx={{ width: 14, height: 12, color: "#4DB5F4" }} />
			) : (
				<VisibilityOffIcon sx={{ width: 14, height: 12, color: "#948E8E", opacity: "50%" }} />
			),
			action: () => {
				dispatch(
					updateComponentInstance(COMPONENT_KEY, instanceId, {
						components: { mainMenu: { showControls: !mainMenuShowControls } }
					})
				);
			},
			hasNestedMenu: false
		},
		// {
		// 	id: 2,
		// 	item: "Detach Viewer",
		// 	icon: (
		// 		<img
		// 			alt="detach_viewer"
		// 			src={require(`orion-components/img/app-icons/icon-open.png`)}
		// 			style={{ width: 14, height: 12 }}
		// 		/>
		// 	),
		// 	action: () => {},
		// 	hasNestedMenu: true
		// },
		// {
		// 	id: 3,
		// 	item: "Remove Viewer",
		// 	icon: (
		// 		<IconButton
		// 			style={{
		// 				color: "black",
		// 				padding: 0,
		// 				boxShadow: "0px 0px 3px #00000026",
		// 				width: 15,
		// 				height: 15,
		// 				borderRadius: "50%",
		// 				opacity: 1,
		// 				transform: "matrix(0, 1, -1, 0, 0, 0)",
		// 				backgroundColor: "#948E8E",
		// 				marginLeft: 4
		// 			}}
		// 		>
		// 			<CloseIcon sx={{ width: 8.5, height: 8.5 }} />
		// 		</IconButton>
		// 	),
		// 	action: () => {},
		// 	hasNestedMenu: false
		// },
		{
			id: 4,
			item: "divider"
		},
		// {
		// 	id: 5,
		// 	item: "Create Situation",
		// 	icon: <WarningIcon sx={{ width: 18.66, height: 18.66, color: "#948E8E" }} />,
		// 	action: () => {},
		// 	hasNestedMenu: false
		// },
		// {
		// 	id: 6,
		// 	item: "Show Situations",
		// 	icon: <VisibilityOffIcon sx={{ width: 18.66, height: 18.66, color: "#948E8E" }} />,
		// 	action: () => {},
		// 	hasNestedMenu: false
		// },
		// {
		// 	id: 8,
		// 	item: "divider"
		// },
		{
			id: 9,
			canAccessMenu: canChangeSettings,
			item: getTranslation("global.profiles.widgets.cameras.camCard.cameraSettings"),
			icon: (
				<div style={{ display: "flex", alignItems: "center" }}>
					<CameraSettings />
				</div>
			),
			action: () => {
				setShowCameraSettings(true);
			},
			hasNestedMenu: false
		},
		{
			id: 10,
			item: getTranslation("global.profiles.widgets.cameras.camCard.presets"),
			canAccessMenu: !tourMode && hasCapability("control") && canControl,
			icon: (
				<div style={{ opacity: "50%" }}>
					<Presets />
				</div>
			),
			action: async () => {
				setNestedMenuIsFor("presets");
				setDisplayNestedMenu(true);
				setNestedMenuContent(presets);
				getMenuState(true);
			},
			hasNestedMenu: true
		},
		{
			id: 11.1,
			item: getTranslation("global.profiles.widgets.cameras.camCard.addToDock"),
			canAccessMenu: !tourMode && !isDockedVideoTour && !cameraIsDocked,
			icon: <Add sx={{ width: 14, height: 12, color: "#FFFFFF", opacity: "50%" }} />,
			action: () => {
				dispatch(addCameraToDockMode(camera, true));
			},
			hasNestedMenu: false
		},
		{
			id: 11,
			item: getTranslation("global.profiles.widgets.cameras.camCard.removeFromDock"),
			canAccessMenu: (!tourMode && cameraIsDocked) || videoTourIsDocked,
			icon: <Clear sx={{ width: 14, height: 12, color: "#FFFFFF", opacity: "50%" }} />,
			action: () => {
				const dockIndex = dockedCameras.lastIndexOf(videoTourIsDocked ? dockedItemId : camera.id);
				if (dockIndex > -1) {
					dispatch(removeDockedCameraAndState(dockIndex, dockedCameras));
				}
			},
			hasNestedMenu: false
		},
		{
			id: 12,
			item: getTranslation("global.CBComponents.newVideoPlayer.components.mainMenu.removeFromWall"),
			canAccessMenu: canManage && isInVideoWall && !isOnAlertProfile && !hideRemoveFromWall,
			icon: <Clear sx={{ width: 14, height: 12, color: "#FFFFFF", opacity: "50%" }} />,
			action: () => {
				const { currentRow, currentCol } = clusterProps;
				removeFromWall(currentRow, currentCol);
			},
			hasNestedMenu: false
		},
		{
			id: 13,
			canAccessMenu: canShowLockMenu,
			item: isLocked
				? getTranslation("global.CBComponents.newVideoPlayer.components.mainMenu.unlock")
				: getTranslation("global.CBComponents.newVideoPlayer.components.mainMenu.lock"),
			icon: isLocked ? (
				<LockOpenIcon sx={{ width: 14, height: 12, color: "#4DB5F4" }} />
			) : (
				<LockIcon sx={{ width: 14, height: 12, color: "#948E8E", opacity: "50%" }} />
			),
			action: () => {
				if (isLocked) {
					// Explicit UNLOCK
					cameraService.clearExplicitLock(camera.id, (err, res) => {
						if (err || !res || res.success === false) {
							const errorMessage =
								(err && err.message) || (res && res.message) || "Failed to clear explicit PTZ lock.";

							dispatch(createUserFeedback(errorMessage, null, true));
							return;
						}

						const successMessage = res.message || "Explicit PTZ lock cleared successfully.";

						dispatch(createUserFeedback(successMessage, null, false));
					});
				} else {
					// Explicit LOCK
					cameraService.setExplicitLock(camera.id, (err, res) => {
						if (err || !res || res.success === false) {
							const errorMessage =
								(err && err.message) || (res && res.message) || "Failed to apply explicit PTZ lock.";

							dispatch(createUserFeedback(errorMessage, null, true));
							return;
						}

						const successMessage = res.message || "Explicit PTZ lock applied successfully.";

						dispatch(createUserFeedback(successMessage, null, false));
					});
				}

				setDisplayMenu(null);
				getMenuState(false);
			},
			hasNestedMenu: false
		},
		{
			id: 14,
			canAccessMenu: supportsClientSnapshot || supportsServerSnapshot,
			item: getTranslation("global.profiles.widgets.cameras.camCard.captureSnapshot"),
			icon: (
				<div>
					<SnapshotCamera />
				</div>
			),
			action: () => {
				handleSnapshotCapture({
					camera,
					mainMenuRef,
					supportsClientSnapshot,
					supportsServerSnapshot,
					cameraService,
					dispatch,
					createUserFeedback,
					getTranslation,
					timeFormat,
					locale
				});
			},
			hasNestedMenu: false
		},
		{
			id: 15,
			canAccessMenu: true,
			item: getTranslation("global.profiles.widgets.cameras.camCard.bookmarks"),
			icon: <Bookmark />,
			action: () => {
				setNestedMenuIsFor("bookmarks");
				setDisplayNestedMenu(true);
				setNestedMenuContent(bookmarks);
				getMenuState(true);
			},
			hasNestedMenu: true
		},
		{
			id: 16,
			canAccessMenu: true,
			item: getTranslation("global.profiles.widgets.cameras.camCard.clips"),
			icon: <Video />,
			action: () => {
				setNestedMenuIsFor("clips");
				setDisplayNestedMenu(true);
				setNestedMenuContent(clips);
				getMenuState(true);
			},
			hasNestedMenu: true
		}
	];

	const handlePopoverClose = () => {
		setDisplayMenu(null);
		getMenuState(false);
	};

	const closeNestedMenu = () => {
		setDisplayNestedMenu(false);
		setDisplayMenu(true);
	};

	const handleAddPreset = () => {
		setShowAddPresetDialog(true);
	};

	const handleDeletePresetClick = preset => {
		setSelectedPresetToDelete(preset);
		setShowDeletePresetDialog(true);
		closeMainMenuAndNestedMenu();
	};

	const handleDeleteBookmarkClick = bookmark => {
		setSelectedBookmarkToDelete(bookmark);
		setShowDeleteBookmarkDialog(true);
		closeMainMenuAndNestedMenu();
	};

	const handleDeleteClipClick = clip => {
		setSelectedClipToDelete(clip);
		setShowDeleteClipDialog(true);
		closeMainMenuAndNestedMenu();
	};

	const closeMainMenuAndNestedMenu = () => {
		setDisplayNestedMenu(false);
		setDisplayMenu(null);
		getMenuState(false);
	};

	const handleSavePresetError = (err, res) => {
		const errorMessage =
			(err && err.message) ||
			(res && res.message) ||
			getTranslation("global.CBComponents.newVideoPlayer.components.mainMenu.failedSavePreset");
		dispatch(createUserFeedback(errorMessage, null, true));
		setShowAddPresetDialog(false);
	};

	const handleSavePresetSuccess = res => {
		setShowAddPresetDialog(false);
		setupPresets();
		dispatch(
			createUserFeedback(
				res.message ||
					getTranslation("global.CBComponents.newVideoPlayer.components.mainMenu.successSavePreset"),
				null,
				false
			)
		);
	};

	const handleDeletePresetError = (err, res) => {
		const errorMessage =
			(err && err.message) ||
			(res && res.message) ||
			getTranslation("global.CBComponents.newVideoPlayer.components.mainMenu.failedDeletePreset");
		dispatch(createUserFeedback(errorMessage, null, true));
	};

	const handleDeletePresetSuccess = res => {
		setShowDeletePresetDialog(false);
		setSelectedPresetToDelete(null);
		setupPresets();
		dispatch(
			createUserFeedback(
				res.message ||
					getTranslation("global.CBComponents.newVideoPlayer.components.mainMenu.successDeletePreset"),
				null,
				false
			)
		);
	};

	const handleDeleteBookmark = () => {
		if (!selectedBookmarkToDelete?.id) return;

		setIsDeletingBookmark(true);
		cameraService.deleteBookmark(camera.id, selectedBookmarkToDelete.id, (err, res) => {
			setIsDeletingBookmark(false);

			if (err || !res || res.success === false) {
				const errorMessage =
					(err && err.message) ||
					(res && res.message) ||
					getTranslation("global.CBComponents.newVideoPlayer.components.mainMenu.failedDeleteBookmark");
				dispatch(createUserFeedback(errorMessage, null, true));
				return;
			}

			setShowDeleteBookmarkDialog(false);
			setSelectedBookmarkToDelete(null);
			const successMessage =
				res.message ||
				getTranslation("global.CBComponents.newVideoPlayer.components.mainMenu.successDeleteBookmark");
			dispatch(createUserFeedback(successMessage, null, false));
			// Refresh bookmarks to ensure the UI reflects the current state
			setupBookmarks();
		});
	};

	const handleDeleteClip = () => {
		if (!selectedClipToDelete?.id) return;

		setIsDeletingClip(true);
		cameraService.deleteVideoClip(camera.id, selectedClipToDelete.id, (err, res) => {
			setIsDeletingClip(false);

			if (err || !res || res.success === false) {
				const errorMessage =
					(err && err.message) ||
					(res && res.message) ||
					getTranslation("global.CBComponents.newVideoPlayer.components.mainMenu.failedDeleteClip");
				dispatch(createUserFeedback(errorMessage, null, true));
				return;
			}

			setShowDeleteClipDialog(false);
			setSelectedClipToDelete(null);
			const successMessage =
				res.message ||
				getTranslation("global.CBComponents.newVideoPlayer.components.mainMenu.successDeleteClip");
			dispatch(createUserFeedback(successMessage, null, false));
			setupVideoClips();
		});
	};

	const handleExportClip = clip => {
		if (!clip?.id || isExportingClip) return;

		setIsExportingClip(true);
		cameraService.exportVideoClip(camera.id, clip.id, (err, res) => {
			setIsExportingClip(false);

			if (err || !res || res.success === false) {
				const errorMessage = getRequestErrorMessage(
					err,
					res,
					getTranslation("global.CBComponents.newVideoPlayer.components.mainMenu.failedStartClipExport")
				);
				dispatch(createUserFeedback(errorMessage, null, true));
				return;
			}

			downloadsService.notifyDownloadStarted(res);
			dispatch(
				createUserFeedback(
					getTranslation("global.CBComponents.newVideoPlayer.components.mainMenu.successStartClipExport"),
					null,
					false
				)
			);
			closeMainMenuAndNestedMenu();
		});
	};

	const handleSavePreset = selectedPresetValue => {
		if (!selectedPresetValue) return;

		setIsSavingPreset(true);
		const parsedPresetNumber = Number.parseInt(selectedPresetValue, 10);
		const presetNumber = Number.isInteger(parsedPresetNumber) ? parsedPresetNumber : selectedPresetValue;

		cameraService.savePreset(camera.id, presetNumber, (err, res) => {
			setIsSavingPreset(false);

			if (err || !res || res.success === false) {
				handleSavePresetError(err, res);
				return;
			}

			handleSavePresetSuccess(res);
		});
	};

	const handleDeletePreset = () => {
		const rawPresetValue = selectedPresetToDelete?.value;
		const parsedPresetValue = Number.parseInt(rawPresetValue, 10);
		const presetValue = Number.isInteger(parsedPresetValue) ? parsedPresetValue : rawPresetValue;
		if (!presetValue) return;

		setIsDeletingPreset(true);
		cameraService.deletePreset(camera.id, presetValue, (err, res) => {
			setIsDeletingPreset(false);

			if (err || !res || res.success === false) {
				handleDeletePresetError(err, res);
				return;
			}

			handleDeletePresetSuccess(res);
		});
	};

	const handleBookmarkSelect = bookmark => {
		onBookmarkSelect && onBookmarkSelect(bookmark);
		closeMainMenuAndNestedMenu();
	};

	const handleClipSelect = clip => {
		onClipSelect && onClipSelect(clip);
		closeMainMenuAndNestedMenu();
	};

	return (
		<div dir={dir} ref={mainMenuRef}>
			<IconButton
				style={{ color: "#FFF", width: "auto", padding: 0, height: "100%" }}
				onClick={() => {
					setDisplayMenu(true);
					getMenuState(true);
				}}
				data-testid="orion-components-cbcomponents-new-video-player-components-main-menu-main-menu-icon-button"
			>
				<MoreVertIcon />
			</IconButton>
			<div
				style={{
					display: displayMenu !== null ? "block" : "none",
					position: "absolute",
					top: 0,
					color: "#fff",
					visibility: displayNestedMenu ? "hidden" : "visible",
					zIndex: 9999
				}}
				onMouseLeave={handlePopoverClose}
				data-testid="orion-components-cbcomponents-new-video-player-components-main-menu-main-menu-div"
			>
				<div
					style={{
						width: 160,
						minHeight: 160,
						maxHeight: 280,
						overflowY: "auto",
						overflowX: "hidden",
						background: "#232223",
						boxShadow: "0px 0px 6px #0000009A",
						opacity: 1,
						backdropFilter: "blur(3px)",
						WebkitBackdropFilter: "blur(3px)",
						cursor: !canControl ? "default" : "pointer",
						borderRadius: 8,
						padding: "4px 0",
						overscrollBehavior: "contain"
					}}
					onWheel={event => event.stopPropagation()}
					data-testid="orion-components-cbcomponents-new-video-player-components-main-menu-main-menu-div-2"
				>
					{menuItems.map((value, index) => {
						if (value.item !== "divider") {
							if (value.canAccessMenu) {
								return (
									<div
										key={index}
										className={`bcc-main-menu ${!canControl && "disabled"}`}
										onClick={value.action}
										style={{
											width: "100%",
											display: "flex",
											flexDirection: "row",
											alignItems: "center",
											justifyContent: "space-between",
											minHeight: 20,
											padding: "2px 5px",
											fontSize: 10,
											lineHeight: "12px",
											cursor: "pointer",
											boxSizing: "border-box"
										}}
										data-testid="orion-components-cbcomponents-new-video-player-components-main-menu-main-menu-div-3"
									>
										<div
											style={{ display: "flex", alignItems: "center", width: 14, flexShrink: 0 }}
										>
											{value.icon}
										</div>
										<div
											style={{
												flex: "1 1 auto",
												display: "flex",
												alignItems: "center",
												marginLeft: 16,
												overflow: "hidden",
												whiteSpace: "nowrap",
												textOverflow: "ellipsis",
												fontFamily: "Roboto"
											}}
										>
											{value.item}
										</div>
										{value.hasNestedMenu && (
											<div style={{ display: "flex", alignItems: "center" }}>
												<KeyboardArrowRightIcon sx={{ width: 12, height: 12, color: "#fff" }} />
											</div>
										)}
									</div>
								);
							}
						} else {
							return (
								<div
									key={index}
									style={{
										width: "90%",
										height: 1,
										backgroundColor: "#444",
										margin: "4px auto"
									}}
								></div>
							);
						}
					})}
				</div>
			</div>
			{displayNestedMenu && (
				<div
					style={{
						zIndex: 9999,
						display: displayNestedMenu !== null ? "block" : "none",
						position: "absolute",
						top: 0,
						color: "#fff"
					}}
					onMouseLeave={() => {
						setDisplayNestedMenu(false);
					}}
					data-testid="orion-components-cbcomponents-new-video-player-components-main-menu-main-menu-div-4"
				>
					<NestedMenu
						nestedMenuContent={nestedMenuContent}
						closeNestedMenu={closeNestedMenu}
						nestedMenuIsFor={nestedMenuIsFor}
						cameraId={camera.id}
						canCreatePreset={canCreatePreset}
						onAddPreset={handleAddPreset}
						onDeletePreset={handleDeletePresetClick}
						onBookmarkSelect={handleBookmarkSelect}
						onDeleteBookmark={canManageCameraFeed ? handleDeleteBookmarkClick : null}
						onClipSelect={handleClipSelect}
						onDeleteClip={canManageCameraFeed ? handleDeleteClipClick : null}
						onExportClip={canManageCameraFeed ? handleExportClip : null}
						closeMainMenu={() => {
							setDisplayMenu(null);
						}}
						isPresetValid={isPresetValid}
						canDeletePreset={canDeletePreset}
						data-testid="orion-components-cbcomponents-new-video-player-components-main-menu-main-menu-nested-menu"
					/>
				</div>
			)}
			{showCameraSettings && (
				<div>
					<CameraSettingsDialog
						open={showCameraSettings}
						onClose={() => setShowCameraSettings(false)}
						camera={camera}
						data-testid="orion-components-cbcomponents-new-video-player-components-main-menu-main-menu-camera-settings-dialog"
					/>
				</div>
			)}
			<AddPresetDialog
				open={showAddPresetDialog}
				onClose={() => setShowAddPresetDialog(false)}
				onSave={handleSavePreset}
				isSaving={isSavingPreset}
				presets={presets}
				isPresetValid={isPresetValid}
				container={dialogContainerRef?.current ?? null}
				data-testid="orion-components-cbcomponents-new-video-player-components-main-menu-main-menu-add-preset-dialog"
			/>
			<DeletePresetDialog
				open={showDeletePresetDialog}
				onClose={() => {
					setShowDeletePresetDialog(false);
					setSelectedPresetToDelete(null);
				}}
				onSave={handleDeletePreset}
				isSaving={isDeletingPreset}
				presetName={selectedPresetToDelete?.label || selectedPresetToDelete?.name || ""}
				container={dialogContainerRef?.current ?? null}
				data-testid="orion-components-cbcomponents-new-video-player-components-main-menu-main-menu-delete-preset-dialog"
			/>
			<DeleteBookmarkDialog
				open={showDeleteBookmarkDialog}
				onClose={() => {
					setShowDeleteBookmarkDialog(false);
					setSelectedBookmarkToDelete(null);
				}}
				onSave={handleDeleteBookmark}
				isSaving={isDeletingBookmark}
				bookmarkName={
					selectedBookmarkToDelete?.description ||
					selectedBookmarkToDelete?.label ||
					selectedBookmarkToDelete?.name ||
					""
				}
				data-testid="orion-components-cbcomponents-new-video-player-components-main-menu-main-menu-delete-bookmark-dialog"
			/>
			<DeleteClipDialog
				open={showDeleteClipDialog}
				onClose={() => {
					setShowDeleteClipDialog(false);
					setSelectedClipToDelete(null);
				}}
				onSave={handleDeleteClip}
				isSaving={isDeletingClip}
				clipName={selectedClipToDelete?.name || selectedClipToDelete?.description || ""}
				data-testid="orion-components-cbcomponents-new-video-player-components-main-menu-main-menu-delete-clip-dialog"
			/>
		</div>
	);
};

const defaultProps = {
	dockedItemId: null,
	isDockedVideoTour: false,
	readOnly: false
};

MainMenu.propTypes = {
	getMenuState: PropTypes.func.isRequired,
	camera: PropTypes.object.isRequired,
	dockedItemId: PropTypes.string,
	isDockedVideoTour: PropTypes.bool,
	readOnly: PropTypes.bool,
	inDock: PropTypes.bool,
	tourMode: PropTypes.bool,
	isInVideoWall: PropTypes.bool,
	removeFromWall: PropTypes.func,
	clusterProps: PropTypes.shape({
		currentRow: PropTypes.number,
		currentCol: PropTypes.number
	}),
	isOnAlertProfile: PropTypes.bool,
	hideRemoveFromWall: PropTypes.bool,
	instanceId: PropTypes.string,
	onBookmarkSelect: PropTypes.func,
	onClipSelect: PropTypes.func,
	dialogContainerRef: PropTypes.shape({
		current: PropTypes.any
	})
};
MainMenu.defaultProps = defaultProps;

export default MainMenu;
